import { Link, NavLink, useLocation } from "react-router-dom";
import { useBillsAvailability } from "@/hooks/useBillsAvailability";
import { useState } from "react";
import {
  Upload, Users, Receipt, Home, LayoutGrid, RefreshCw,
  Building2, MapPin, User as UserIcon, AlertTriangle, LogOut, ShieldOff, Wallet,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { asText } from "@/lib/format";
import { TID } from "@/constants/testIds";
import { useSession } from "@/contexts/SessionContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

function NavItem({ to, icon: Icon, label, testId, end, disabled, title }) {
  if (disabled) {
    return (
      <span
        data-testid={testId}
        title={title || "Not available"}
        className="flex shrink-0 items-center gap-2 px-3 py-1.5 rounded-md text-[13px] font-medium text-archival-muted cursor-not-allowed whitespace-nowrap"
      >
        <Icon size={16} strokeWidth={1.75} />
        <span>{label}</span>
      </span>
    );
  }
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) =>
        cn(
          "flex shrink-0 items-center gap-2 px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors whitespace-nowrap",
          isActive
            ? "bg-archival-soft text-archival-primary"
            : "text-archival-secondary hover:text-archival-primary hover:bg-archival-soft"
        )
      }
    >
      <Icon size={16} strokeWidth={1.75} />
      <span>{label}</span>
    </NavLink>
  );
}

export default function AppShell({ children }) {
  const location = useLocation();
  const fullBleed = location.pathname.startsWith("/candidates/");
  // The Validation Dashboard intentionally supports "All my outlets" view
  // and does not require outlet selection. Suppress the nag banner there.
  const outletOptional = location.pathname.startsWith("/validation");
  const {
    me, user, org, outletIds, outlets, currentOutletId, capabilities, has,
    setOutletId, signOut,
  } = useSession();

  const { state: billsState, ready: billsReady, retry: retryBills } = useBillsAvailability();
  const noOutlet = !currentOutletId;
  // Mobile-first Bills product renders its own compact chrome.
  if (location.pathname === "/bills" || location.pathname.startsWith("/bills/") || location.pathname === "/expenses") return <div className="min-h-screen bg-archival-base">{children}</div>;
  const canUpload = has("BULK_CAPTURE_UPLOAD");
  const canReview = has("BULK_CAPTURE_REVIEW");
  const outletList = (outlets && outlets.length ? outlets : outletIds.map((id) => ({ id, name: id })));
  // Operator chrome shows human-readable identity only. Raw org/outlet/user ids stay in the session model
  // (API calls, tenancy, audit, logs) and are surfaced on the technical surface: More -> Engine health.
  const currentOutletName = outletList.find((o) => o.id === currentOutletId)?.name || "Selected outlet";

  return (
    <div className="min-h-screen bg-archival-base">
      <header className="border-b border-archival bg-archival-surface sticky top-0 z-40">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-2 xl:py-0 xl:h-16 flex flex-wrap xl:flex-nowrap items-center justify-between gap-x-4 gap-y-2">
          <Link to="/" data-testid={TID.shellBrand} className="flex items-center gap-3 shrink-0">
            <img
              src="/1os-logo.png"
              alt="1OS"
              className="w-10 h-10 rounded-md object-contain bg-black"
              draggable={false}
            />
            <div className="flex flex-col leading-tight">
              <span className="text-[15px] font-semibold text-archival-primary tracking-tight">
                1OS Bills
              </span>
              <span className="text-[11px] text-archival-muted">Bills, purchases and expenses</span>
            </div>
          </Link>

          <nav className="order-3 xl:order-none w-full xl:w-auto xl:shrink-0 flex flex-wrap xl:flex-nowrap items-center gap-1 xl:overflow-x-auto no-scrollbar">
            <NavItem to="/" icon={Home} label="Home" testId="nav-home" end
              disabled={noOutlet} title="Select outlet first" />
            <NavItem to="/bills" icon={Receipt} label="Bills" testId="nav-bills"
              disabled={noOutlet || !canReview || !billsReady}
              title={!billsReady ? "Bills is temporarily unavailable" : "Select outlet first"} />
            <NavItem to="/capture" icon={Upload} label="Capture" testId={TID.navUpload}
              disabled={noOutlet || !canUpload}
              title={!canUpload ? "Missing BULK_CAPTURE_UPLOAD" : "Select outlet first"} />
            <NavItem to="/vendors" icon={Users} label="Vendors"
              testId={TID.navVendorDesk}
              disabled={noOutlet || !canReview} />
            <NavItem to="/expenses" icon={Wallet} label="Expenses" testId="nav-expenses"
              disabled={noOutlet || !canReview || !billsReady}
              title={!billsReady ? "Expenses is temporarily unavailable" : "Select outlet first"} />
            <NavItem to="/more" icon={LayoutGrid} label="More" testId="nav-more"
              disabled={!canReview} title="Review, diagnostics and history" />
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <div
              data-testid="tenant-context"
              className="hidden md:flex flex-col items-end leading-tight text-right min-w-0 max-w-[200px]"
            >
              <span
                data-testid="tenant-org"
                className="text-[12px] font-semibold text-archival-primary flex items-center gap-1 min-w-0"
              >
                <Building2 size={12} className="text-archival-muted shrink-0" />
                <span className="truncate">{org?.name || "Organisation"}</span>
              </span>
              <span
                data-testid="tenant-outlet"
                className={cn(
                  "text-[11px] flex items-center gap-1 font-medium min-w-0",
                  currentOutletId ? "text-archival-secondary" : "text-amber-700"
                )}
              >
                <MapPin size={11} className="shrink-0" />
                {currentOutletId
                  ? <span className="truncate">{currentOutletName}</span>
                  : <span className="italic">No outlet selected</span>}
              </span>
              {user?.email && (
                <span
                  data-testid="tenant-user"
                  className="text-[11px] text-archival-muted flex items-center gap-1 min-w-0"
                >
                  <UserIcon size={10} className="shrink-0" />
                  <span className="truncate">{user.email}</span>
                </span>
              )}
            </div>

            <Select
              value={currentOutletId || ""}
              onValueChange={(v) => setOutletId(v || null)}
              disabled={outletList.length === 0}
            >
              <SelectTrigger
                data-testid="outlet-switcher"
                className="w-[180px] xl:w-[220px] h-9 border-archival bg-white text-[12px]"
              >
                <SelectValue placeholder={outletList.length === 0 ? "No outlet access" : "Select outlet…"} />
              </SelectTrigger>
              <SelectContent>
                {outletList.length === 0 && (
                  <div className="px-2 py-1 text-[12px] text-archival-muted">No outlets assigned</div>
                )}
                {outletList.map((o) => (
                  <SelectItem
                    key={o.id}
                    value={o.id}
                    data-testid={`outlet-option-${o.id}`}
                  >
                    {o.name || "Outlet"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              data-testid="sign-out"
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-archival-secondary hover:text-archival-primary"
              onClick={signOut}
              title="Sign out"
            >
              <LogOut size={14} />
            </Button>
          </div>
        </div>

        {noOutlet && outletIds.length > 0 && !outletOptional && (
          <div
            data-testid="outlet-blocking-banner"
            className="bg-amber-50 border-t border-amber-200 text-amber-900"
          >
            <div className="max-w-[1280px] mx-auto px-6 py-2 flex items-center gap-2 text-[13px]">
              <AlertTriangle size={14} />
              <span className="font-medium">Select outlet before uploading bills.</span>
              <span className="text-[12px] text-amber-800/80">
                Pick an outlet from the switcher to enable upload and review.
              </span>
            </div>
          </div>
        )}
        {outletIds.length === 0 && me && (
          <div
            data-testid="no-outlet-access-banner"
            className="bg-red-50 border-t border-red-200 text-red-800"
          >
            <div className="max-w-[1280px] mx-auto px-6 py-2 flex items-center gap-2 text-[13px]">
              <ShieldOff size={14} />
              <span className="font-medium">No outlet access found.</span>
              <span className="text-[12px]">Contact your administrator.</span>
            </div>
          </div>
        )}
        {/* Expense Capture is retired: a Bills infrastructure problem is reported as a BILLS problem, never
            as an EC validation failure. Bills and Expenses stay in the nav throughout — only actions pause. */}
        {!noOutlet && billsState === "unavailable" && (
          <div
            data-testid="bills-availability-banner"
            className="bg-amber-50 border-t border-amber-200 text-amber-900"
          >
            <div className="max-w-[1280px] mx-auto px-6 py-2 flex items-center gap-2 text-[13px] flex-wrap">
              <AlertTriangle size={14} />
              <span className="font-medium">Bills is temporarily unavailable.</span>
              <span className="text-[12px]">
                We couldn&apos;t reach the Bills datastore. Your existing data has not been changed.
              </span>
              <button
                type="button"
                onClick={retryBills}
                data-testid="bills-availability-retry"
                className="ml-auto inline-flex items-center gap-1 rounded px-2 py-1 text-[12px] font-medium border border-amber-300 hover:bg-amber-100 transition-colors"
              >
                <RefreshCw size={12} /> Retry
              </button>
            </div>
          </div>
        )}
      </header>
      <main className={cn(fullBleed ? "" : "max-w-[1280px] mx-auto px-6 py-8")}>{children}</main>
    </div>
  );
}
